import { CardRow } from 'components/CardRow';
import { Highlight } from 'components/Highlight';
import { ICONS } from 'components/Icon';
import { ModalScreens, Screens } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { Chip } from 'screens/AssessmentScreen/Finding/Chip';
import { Finding } from 'screens/AssessmentScreen/Finding/Finding';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  selectAseCategories,
  selectAseControlAstAse,
  selectAseTplSuggestedControl,
  selectAseExplorerOptionFindingReference,
  selectAseControlStatuses,
  selectAseExplorerOptionSize,
  selectAseExplorerOptionFindingDescriptions,
  selectAseExplorerOptionFindingAdditional,
  selectAseExplorerOptionFindingSuggestionDescriptions,
  selectAseExplorerOptionFindingSuggestionAdditional,
} from 'store/assessments/selectors';
import { returnDefaultControl } from 'store/assessments/templates';
import {
  AssessmentItemTypes,
  InstanceParentParams,
} from 'store/assessments/types';
import {
  doAseDeleteControl,
  doAseOpenControl,
} from 'store/assessments/actions';
import { rbAddReferencePrefix } from 'lib/riskbase';
import { useConfirm } from 'hooks/useConfirm';
import { theme } from 'lib/theme';
import { Txt } from 'components/Txt';

export const ExplorerControlRow: React.FC<{
  id: string;
  parent: InstanceParentParams;
}> = React.memo(({ id, parent }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const explorerOptionFindingDescriptions = useSelector(
    selectAseExplorerOptionFindingDescriptions,
  );
  const explorerOptionFindingAdditional = useSelector(
    selectAseExplorerOptionFindingAdditional,
  );
  const explorerOptionFindingRef = useSelector(
    selectAseExplorerOptionFindingReference,
  );
  const explorerOptionSize = useSelector(selectAseExplorerOptionSize);

  const control = useSelector(
    (state) => selectAseControlAstAse(state, id),
    isEqual,
  );
  const categories = useSelector(selectAseCategories);
  const statuses = useSelector(selectAseControlStatuses);

  if (!control) {
    return null;
  }

  const milestone =
    !!control.milestone && control.milestone + 1000 > Date.now();
  const category = categories[control.categoryId] ?? categories['0']!;

  const chips = (explorerOptionFindingRef ||
    (explorerOptionFindingAdditional &&
      (category.id !== '0' ||
        !!control.where ||
        !!control.photoIds.length))) && (
    <>
      {explorerOptionFindingRef && (
        <Chip size={explorerOptionSize} iconSource={ICONS.RBPlectrumSolid}>
          {control.reference ? (
            `${rbAddReferencePrefix(control.reference)}`
          ) : (
            <Txt size="xxxsmall" weight="bold">
              {rbAddReferencePrefix('')}
              <Txt
                size="xxxsmall"
                weight="bold"
                style={{ color: theme.colors.placeholder }}
              >
                000000
              </Txt>
            </Txt>
          )}
        </Chip>
      )}
      {explorerOptionFindingAdditional && category.id !== '0' && (
        <Chip size={explorerOptionSize} iconSource={ICONS.TagLight}>
          {category.name}
        </Chip>
      )}
      {explorerOptionFindingAdditional && !!control.where && (
        <Chip size={explorerOptionSize} iconSource={ICONS.LocationArrowLight}>
          {control.where}
        </Chip>
      )}
      {explorerOptionFindingAdditional && !!control.photoIds.length && (
        <Chip size={explorerOptionSize} iconSource={ICONS.ImageLight} />
      )}
    </>
  );

  return (
    <Highlight highlight={milestone}>
      <CardRow
        compact={explorerOptionSize === 'small'}
        small={explorerOptionSize === 'small'}
        navigationIsSelected={
          control.fieldInstanceId
            ? { name: Screens.ASEREPEATABLE_CONTROL, params: { id, parent } }
            : { name: Screens.ASE_CONTROL, params: { id, parent } }
        }
        onPress={() => dispatch(doAseOpenControl({ id, parent }))}
        hideMenuButton
        menuOptions={[
          {
            text: control.isReview ? 'Delete Review' : 'Delete Control',
            destructive: true,
            icon: ICONS.TrashCanLight,
            onPress: () =>
              confirm({
                text: control.isReview
                  ? 'Are you sure you want to delete the review for this control?'
                  : 'Are you sure you want to delete this control?',
                title: control.isReview ? 'Delete Review' : 'Delete Control',
                confirmText: 'Delete',
                onConfirm: () =>
                  dispatch(doAseDeleteControl({ id: control.id })),
                destructive: true,
              }),
          },
        ]}
      >
        <Finding
          isUnreviewed={control.isReview && control.reviewStatusId === null}
          description={control.description}
          why={control.why}
          superscript={
            control.reviewStatusId
              ? `Reviewed as ${
                  statuses.find(
                    (status) => status.id === control.reviewStatusId,
                  )?.name
                }`
              : undefined
          }
          chips={chips}
          type={AssessmentItemTypes.CONTROL}
          descriptionNumberOfLines={
            explorerOptionFindingDescriptions === 'short' ? 2 : 0
          }
          size={explorerOptionSize}
          cancelled={control.reviewStatusId === '4'}
        />
      </CardRow>
    </Highlight>
  );
});

export const AddCustomControl: React.FC<{
  parentId: string;
  parentType: 'section' | 'question';
  fieldInstanceId: string | null;
  parent: InstanceParentParams;
}> = ({ parentId, parentType, fieldInstanceId, parent }) => {
  const dispatch = useDispatch();
  const explorerOptionSize = useSelector(selectAseExplorerOptionSize);
  return (
    <CardRow
      compact={explorerOptionSize === 'small'}
      onPress={() => {
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.AseControl,
            params: {
              id: '',
              parent,
              data: returnDefaultControl({
                sectionId: parentType === 'section' ? parentId : null,
                questionId: parentType === 'question' ? parentId : null,
                fieldInstanceId,
                suggestionId: null,
              }),
            },
          }),
        );
      }}
    >
      <Finding
        description="Add Control"
        why=""
        type={AssessmentItemTypes.CONTROL}
        size={explorerOptionSize}
        isAddCustom
      />
    </CardRow>
  );
};

export const AddTemplateControl: React.FC<{
  templateId: string;
  parentId: string;
  parentType: 'section' | 'question';
  fieldInstanceId: string | null;
  parent: InstanceParentParams;
}> = ({ templateId, parentId, parentType, fieldInstanceId, parent }) => {
  const dispatch = useDispatch();
  const explorerOptionFindingDescriptions = useSelector(
    selectAseExplorerOptionFindingSuggestionDescriptions,
  );
  const explorerOptionFindingAdditional = useSelector(
    selectAseExplorerOptionFindingSuggestionAdditional,
  );
  const explorerOptionSize = useSelector(selectAseExplorerOptionSize);
  const categories = useSelector(selectAseCategories);
  const control = useSelector((state) =>
    selectAseTplSuggestedControl(state, templateId, fieldInstanceId),
  );

  if (!control) {
    return null;
  }

  const category = categories[control.categoryId] ?? categories['0']!;

  const chips = explorerOptionFindingAdditional && category.id !== '0' && (
    <Chip size={explorerOptionSize} iconSource={ICONS.TagLight}>
      {category.name}
    </Chip>
  );

  return (
    <CardRow
      compact={explorerOptionSize === 'small'}
      onPress={() => {
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.AseControl,
            params: {
              id: '',
              parent,
              data: {
                ...returnDefaultControl({
                  sectionId: parentType === 'section' ? parentId : null,
                  questionId: parentType === 'question' ? parentId : null,
                  fieldInstanceId,
                  suggestionId: control.id,
                }),
                description: control.description,
                why: control.why,
                categoryId: control.categoryId,
              },
            },
          }),
        );
      }}
    >
      <Finding
        description={control.description}
        why={control.why}
        chips={chips}
        type={AssessmentItemTypes.CONTROL}
        isSuggestion
        countSuggestions={control.usedCount}
        size={explorerOptionSize}
        descriptionNumberOfLines={
          explorerOptionFindingDescriptions === 'short' ? 2 : 0
        }
      />
    </CardRow>
  );
};
